export interface ColumnInfo {
  name: string;
  type: 'numeric' | 'categorical' | 'datetime' | 'boolean';
  uniqueValues: number;
  nullCount: number;
  min?: number;
  max?: number;
  mean?: number;
  categories?: string[];
}

export interface ChartRecommendation {
  type: string;
  title: string;
  description: string;
  columns: string[];
  priority: number;
}

export class DataAnalyzer {
  private data: any[];
  private headers: string[];
  private columnInfo: Map<string, ColumnInfo> = new Map();

  constructor(data: any[], headers: string[]) {
    this.data = data;
    this.headers = headers;
    this.analyzeColumns();
  }

  private analyzeColumns() {
    this.headers.forEach(header => {
      const values = this.data.map(row => row[header]).filter(val => val !== '' && val != null);
      const uniqueValues = new Set(values);
      
      const columnInfo: ColumnInfo = {
        name: header,
        type: this.inferDataType(values),
        uniqueValues: uniqueValues.size,
        nullCount: this.data.length - values.length
      };

      if (columnInfo.type === 'numeric') {
        const numericValues = values.map(v => Number(v)).filter(v => !isNaN(v));
        columnInfo.min = Math.min(...numericValues);
        columnInfo.max = Math.max(...numericValues);
        columnInfo.mean = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
      } else if (columnInfo.type === 'categorical') {
        columnInfo.categories = Array.from(uniqueValues).slice(0, 20) as string[];
      }

      this.columnInfo.set(header, columnInfo);
    });
  }

  private inferDataType(values: any[]): 'numeric' | 'categorical' | 'datetime' | 'boolean' {
    if (values.length === 0) return 'categorical';

    const sample = values.slice(0, Math.min(100, values.length));
    let numericCount = 0;
    let dateCount = 0;
    let booleanCount = 0;

    sample.forEach(value => {
      const str = String(value).trim().toLowerCase();

      if (['true', 'false', 'yes', 'no', '1', '0'].includes(str)) {
        booleanCount++;
      } else if (!isNaN(Number(value)) && value !== '' && value !== null) {
        numericCount++;
      } else if (this.isDate(str)) {
        dateCount++;
      }
    });

    const total = sample.length;
    if (numericCount / total > 0.8) return 'numeric';
    if (dateCount / total > 0.6) return 'datetime';
    if (booleanCount / total > 0.8) return 'boolean';
    
    return 'categorical';
  }

  private isDate(value: string): boolean {
    const date = new Date(value);
    return !isNaN(date.getTime()) && value.length > 4;
  }

  getColumnInfo(): Map<string, ColumnInfo> {
    return this.columnInfo;
  }

  getChartRecommendations(): ChartRecommendation[] {
    const recommendations: ChartRecommendation[] = [];
    const numericColumns = this.getPriorityNumericColumns();
    const categoricalColumns = this.getPriorityCategoricalColumns();
    const datetimeColumns = this.getColumnsByType('datetime');
    const preferredPairs = this.getPreferredNumericPairs(numericColumns);

    if (preferredPairs.length > 0) {
      const [x, y] = preferredPairs[0];
      recommendations.push({
        type: 'scatter',
        title: `${y} vs ${x}`,
        description: 'Scatter plot highlighting relationship between high-impact metrics',
        columns: [x, y],
        priority: 10
      });
    } else if (numericColumns.length >= 2) {
      recommendations.push({
        type: 'scatter',
        title: `${numericColumns[0]} vs ${numericColumns[1]}`,
        description: 'Scatter plot showing correlation between two numeric variables',
        columns: [numericColumns[0], numericColumns[1]],
        priority: 8
      });
    }

    if (numericColumns.length >= 1 && categoricalColumns.length >= 1) {
      recommendations.push({
        type: 'bar',
        title: `Average ${numericColumns[0]} by ${categoricalColumns[0]}`,
        description: 'Bar chart comparing category-level averages for a key metric',
        columns: [categoricalColumns[0], numericColumns[0]],
        priority: 9
      });

      recommendations.push({
        type: 'box',
        title: `${numericColumns[0]} Distribution by ${categoricalColumns[0]}`,
        description: 'Box plot showing distribution of numeric values by category',
        columns: [categoricalColumns[0], numericColumns[0]],
        priority: 8
      });
    }

    if (numericColumns.length >= 1) {
      recommendations.push({
        type: 'histogram',
        title: `${numericColumns[0]} Distribution`,
        description: 'Histogram showing the distribution of values',
        columns: [numericColumns[0]],
        priority: 6
      });
    }

    if (categoricalColumns.length >= 1) {
      const topCategorical = categoricalColumns.find(col => {
        const info = this.columnInfo.get(col);
        return info && info.uniqueValues <= 20;
      });
      
      if (topCategorical) {
        recommendations.push({
          type: 'pie',
          title: `${topCategorical} Distribution`,
          description: 'Pie chart showing the proportion of each category',
          columns: [topCategorical],
          priority: 5
        });
      }
    }

    if (datetimeColumns.length >= 1 && numericColumns.length >= 1) {
      recommendations.push({
        type: 'line',
        title: `${numericColumns[0]} Over Time`,
        description: 'Line chart showing trends over time',
        columns: [datetimeColumns[0], numericColumns[0]],
        priority: 9
      });
    }

    if (numericColumns.length >= 3 && preferredPairs.length === 0) {
      recommendations.push({
        type: 'scatter3d',
        title: `3D Scatter: ${numericColumns[0]}, ${numericColumns[1]}, ${numericColumns[2]}`,
        description: '3D scatter plot showing relationships between three variables',
        columns: [numericColumns[0], numericColumns[1], numericColumns[2]],
        priority: 4
      });
    }

    if (numericColumns.length >= 2) {
      recommendations.push({
        type: 'heatmap',
        title: 'Correlation Heatmap (Top Metrics)',
        description: 'Heatmap showing correlations between numeric variables',
        columns: numericColumns.slice(0, 8),
        priority: 8
      });
    }

    if (categoricalColumns.length >= 2) {
      recommendations.push({
        type: 'sunburst',
        title: `${categoricalColumns[0]} and ${categoricalColumns[1]} Hierarchy`,
        description: 'Sunburst chart showing hierarchical categorical data',
        columns: [categoricalColumns[0], categoricalColumns[1]],
        priority: 5
      });
    }

    if (numericColumns.length >= 1 && categoricalColumns.length >= 1) {
      recommendations.push({
        type: 'violin',
        title: `${numericColumns[0]} Distribution by ${categoricalColumns[0]}`,
        description: 'Violin plot showing detailed distribution by category',
        columns: [categoricalColumns[0], numericColumns[0]],
        priority: 5
      });
    }

    return recommendations.sort((a, b) => b.priority - a.priority).slice(0, 10);
  }

  private getPriorityNumericColumns(): string[] {
    return this.getColumnsByType('numeric')
      .filter((name) => {
        const info = this.columnInfo.get(name);
        return info ? !this.isLikelyIdentifier(name, info) : true;
      })
      .sort((a, b) => this.numericColumnScore(b) - this.numericColumnScore(a));
  }

  private getPriorityCategoricalColumns(): string[] {
    const candidates = Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'categorical' || info.type === 'boolean')
      .map(([name]) => name);

    return candidates
      .filter((name) => {
        const info = this.columnInfo.get(name);
        if (!info) return false;
        if (this.isLikelyIdentifier(name, info)) return false;
        if (info.uniqueValues > Math.min(20, Math.max(6, Math.floor(this.data.length * 0.2)))) return false;
        return true;
      })
      .sort((a, b) => this.categoricalColumnScore(b) - this.categoricalColumnScore(a));
  }

  private getPreferredNumericPairs(numericColumns: string[]): [string, string][] {
    const has = (name: string) => numericColumns.includes(name);
    const pairs: [string, string][] = [];

    if (has('productivity_score') && has('exam_score')) {
      pairs.push(['productivity_score', 'exam_score']);
    }
    if (has('focus_index') && has('productivity_score')) {
      pairs.push(['focus_index', 'productivity_score']);
    }
    if (has('burnout_level') && has('productivity_score')) {
      pairs.push(['burnout_level', 'productivity_score']);
    }
    if (has('study_hours') && has('exam_score')) {
      pairs.push(['study_hours', 'exam_score']);
    }

    return pairs;
  }

  private isLikelyIdentifier(name: string, info: ColumnInfo): boolean {
    const lower = name.toLowerCase();
    const rowCount = Math.max(this.data.length, 1);
    const uniqueRatio = info.uniqueValues / rowCount;

    if (/(^id$|_id$|id_|uuid|identifier|student_id|record_id)/.test(lower)) {
      return true;
    }

    return uniqueRatio > 0.98 && (info.type === 'numeric' || info.type === 'categorical');
  }

  private numericColumnScore(name: string): number {
    const info = this.columnInfo.get(name);
    if (!info) return -Infinity;
    if (this.isLikelyIdentifier(name, info)) return -Infinity;

    const lower = name.toLowerCase();
    let score = 0;

    if (/productivity|exam|focus|burnout|mental_health|score|index|level/.test(lower)) score += 6;
    if (/study|sleep|screen|exercise|hours|caffeine/.test(lower)) score += 3;
    if (/age/.test(lower)) score += 1;

    if (typeof info.mean === 'number' && typeof info.max === 'number' && typeof info.min === 'number') {
      const range = Math.abs(info.max - info.min);
      const relativeRange = Math.abs(info.mean) > 0 ? range / Math.abs(info.mean) : range;
      score += Math.min(relativeRange, 5);
    }

    const completeness = 1 - (info.nullCount / Math.max(this.data.length, 1));
    score += completeness * 2;
    return score;
  }

  private categoricalColumnScore(name: string): number {
    const info = this.columnInfo.get(name);
    if (!info) return -Infinity;
    if (this.isLikelyIdentifier(name, info)) return -Infinity;

    const lower = name.toLowerCase();
    let score = 0;
    if (/gender|academic|internet|quality|deadline|part_time|job/.test(lower)) score += 5;

    const uniquePenalty = info.uniqueValues > 15 ? 2 : info.uniqueValues > 8 ? 1 : 0;
    score -= uniquePenalty;

    const completeness = 1 - (info.nullCount / Math.max(this.data.length, 1));
    score += completeness * 2;
    return score;
  }

  private getColumnsByType(type: string): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === type)
      .map(([name]) => name);
  }
}
